import type { GlyphKey, Star } from '@origami/shared'

/**
 * TIERED_GLYPHS — per-(glyph, star) art registry.
 *
 * Add an entry as soon as a tier asset ships. Missing entries fall back
 * to the base GLYPHS[glyph] silhouette via TieredCritter, so the system
 * is usable from day one with no per-tier art at all.
 *
 * Two source-image flavors are supported:
 *
 *   A) ONE-FIGURE-PER-FILE — drop `assets/<glyph>-<tier>.jpg`, add a
 *      job to `packages/client/scripts/strip-white-bg.mjs`, run it,
 *      then register the resulting path below.
 *
 *   B) MULTI-TIER SHEET (e.g. 3 figures side-by-side on white) — drop
 *      `assets/<unit>_<N>tier.jpg`, add one job per tier to
 *      `packages/client/scripts/extract-tier-art.mjs` (uses crop
 *      rects with overlap + largest-component pruning), run it, then
 *      register the paths below.
 *
 * Tier 1 entries are optional: if missing, TieredCritter falls back to
 * the base GLYPHS[glyph] silhouette (handy when tier 1 lives in the
 * single-figure flow and tiers 2+ come from a multi-tier sheet).
 */
export const TIERED_GLYPHS: Partial<Record<GlyphKey, Partial<Record<Star, string>>>> = {
  // Ash Wolf — all three tiers from assets/new_wolf_3tier.jpg.
  // Tier 1 explicit so shop card / board ★1 / encyclopedia share the
  // bitmap (the legacy single-figure /glyph-assets/wolf.png is no
  // longer the active tier-1 source).
  wolf: {
    1: '/glyph-assets/wolf-1.png',
    2: '/glyph-assets/wolf-2.png',
    3: '/glyph-assets/wolf-3.png',
  },
  // Stag (Court Stag) — all three tiers from assets/deer_3tier.jpg.
  // Tier 1 is registered explicitly here (rather than relying on the
  // UnitGlyph fallback) so the lookup hits the same ImgGlyph path
  // both before purchase (shop card / encyclopedia) and after
  // (board slot at ★1).
  stag: {
    1: '/glyph-assets/stag-1.png',
    2: '/glyph-assets/stag-2.png',
    3: '/glyph-assets/stag-3.png',
  },
  // Fox (Tinder Fox) — all three tiers from assets/fox_3tier.jpg.
  // Same explicit-tier-1 pattern as stag so shop preview, board ★1,
  // and encyclopedia all share the same tier-1 art rather than the
  // legacy single-figure fox.png from strip-white-bg.mjs.
  fox: {
    1: '/glyph-assets/fox-1.png',
    2: '/glyph-assets/fox-2.png',
    3: '/glyph-assets/fox-3.png',
  },
  // Mantis (Mantis Acolyte) — all three tiers from
  // assets/mantis_3tier.jpg. Tier 1 explicit so the shop card / board
  // ★1 / encyclopedia all share the same bitmap rather than the old
  // SVG silhouette in Mantis.tsx (now an ImgGlyph wrapper around
  // mantis-1.png).
  mantis: {
    1: '/glyph-assets/mantis-1.png',
    2: '/glyph-assets/mantis-2.png',
    3: '/glyph-assets/mantis-3.png',
  },
  // Moth (Ember Moth) — all three tiers from assets/ember_moth_3tier.jpg.
  // The `moth` glyph key was added specifically for this unit (the
  // generic `butterfly` glyph stays available for any future
  // butterfly-flavored unit). Tier 1 explicit so shop card / board ★1
  // / encyclopedia all share the bitmap.
  moth: {
    1: '/glyph-assets/moth-1.png',
    2: '/glyph-assets/moth-2.png',
    3: '/glyph-assets/moth-3.png',
  },
  // Pup (Pebble Pup) — all three tiers from assets/pebble_pup_3tier.jpg.
  // White-strip (not achromatic) because the pup is grayscale paper on
  // a white backdrop. Tier 1 explicit so shop card / board ★1 /
  // encyclopedia all share the bitmap.
  pup: {
    1: '/glyph-assets/pup-1.png',
    2: '/glyph-assets/pup-2.png',
    3: '/glyph-assets/pup-3.png',
  },
  // Ray (Sky Ray) — all three tiers from assets/sky_ray_3tier.jpg.
  // The `ray` glyph key was added specifically for this unit (the
  // generic `fish` glyph stays shared by Ancient Koi and Stream
  // Smelt — overloading `fish` here would hijack their renders).
  // Tier 3 cream wing panels survive thanks to the interior-fill
  // pass in the extractor.
  ray: {
    1: '/glyph-assets/ray-1.png',
    2: '/glyph-assets/ray-2.png',
    3: '/glyph-assets/ray-3.png',
  },
  // Owl (Sage Owl) — all three tiers from assets/sage_owl_3tier.jpg.
  // Default white-strip works cleanly (warm-brown body chroma 56-81,
  // lightest face cream lum 194 < FEATHER_FLOOR=200, backdrop 240+).
  // Tier 1 explicit so shop card / board ★1 / encyclopedia all share
  // the bitmap.
  owl: {
    1: '/glyph-assets/owl-1.png',
    2: '/glyph-assets/owl-2.png',
    3: '/glyph-assets/owl-3.png',
  },
  // Phoenix — all three tiers from assets/phoenix_3tier.jpg.
  // Achromatic strip handles the warm-cream backdrop; bodies are
  // strongly chromatic (saturated reds / oranges / yellows / purples)
  // so no interior-fill needed.
  phoenix: {
    1: '/glyph-assets/phoenix-1.png',
    2: '/glyph-assets/phoenix-2.png',
    3: '/glyph-assets/phoenix-3.png',
  },
  // Mouse (Pocket Mouse) — all three tiers from
  // assets/pocket_mouse_3tier.jpg. White-strip (not achromatic): the
  // mouse is grayscale paper on a white backdrop.
  mouse: {
    1: '/glyph-assets/mouse-1.png',
    2: '/glyph-assets/mouse-2.png',
    3: '/glyph-assets/mouse-3.png',
  },
  // Mite (Paper Mite) — all three tiers from assets/paper_mite_3tier.jpg.
  // The `mite` glyph key was added specifically for this unit (the
  // generic `beetle` glyph stays available for any future real-beetle
  // unit). White-strip required: achromatic would erase the ★3 grey
  // accent panel (chroma 13).
  mite: {
    1: '/glyph-assets/mite-1.png',
    2: '/glyph-assets/mite-2.png',
    3: '/glyph-assets/mite-3.png',
  },
  // Tadpole (Spore Tadpole) — all three tiers from
  // assets/spore_tadpole_3tier.jpg. The `tadpole` glyph key was added
  // for this unit; `frog` stays free for a future real frog unit.
  // White-strip required: ★1 grey body sits at chroma ~22, near the
  // achromatic floor — luminance-based stripping is the safer rule.
  tadpole: {
    1: '/glyph-assets/tadpole-1.png',
    2: '/glyph-assets/tadpole-2.png',
    3: '/glyph-assets/tadpole-3.png',
  },
  // Salamander — all three tiers from
  // assets/salamander_coil_3tier.jpg. Default white-strip works
  // cleanly (warm chromatic browns, lightest belly cream lum 178 <
  // feather floor 200).
  salamander: {
    1: '/glyph-assets/salamander-1.png',
    2: '/glyph-assets/salamander-2.png',
    3: '/glyph-assets/salamander-3.png',
  },
  // Smelt (Stream Smelt) — all three tiers from
  // assets/stream_smelt_3tier.jpg. The `smelt` glyph key was added for
  // this unit so it doesn't hijack Ancient Koi's `fish` glyph. White-
  // strip + interior fill: silvery-white body has low chroma and
  // partially overlaps the feather band, so interior fill rescues
  // the brightest enclosed spots.
  smelt: {
    1: '/glyph-assets/smelt-1.png',
    2: '/glyph-assets/smelt-2.png',
    3: '/glyph-assets/smelt-3.png',
  },
  // Tortoise (Stone Tortoise) — all three tiers from
  // assets/stone_tortoise_3tier.jpg. Default white-strip works
  // cleanly (warm chromatic browns, no interior light regions).
  tortoise: {
    1: '/glyph-assets/tortoise-1.png',
    2: '/glyph-assets/tortoise-2.png',
    3: '/glyph-assets/tortoise-3.png',
  },
  // Koi (Ancient Koi) — all three tiers from assets/ancient_koi_3tier.jpg.
  // The `koi` glyph key was added for this unit; `fish` stays free for
  // a future generic fish unit. Achromatic strip: bodies are deeply
  // saturated reds/golds/navy on a near-achromatic cream backdrop.
  koi: {
    1: '/glyph-assets/koi-1.png',
    2: '/glyph-assets/koi-2.png',
    3: '/glyph-assets/koi-3.png',
  },
  // Flock (Shadow Crane) — all three tiers from
  // assets/shadow_crane_3tier.png. The unit was renamed from "Crane
  // Flock" to "Shadow Crane"; the glyph key + filenames stayed
  // `flock` to avoid churn across data/tests/fixtures. The generic
  // `crane` glyph stays free for project-wide fallback rendering
  // (UnitGlyph / BattleScreen / Gallery use it as the safety net
  // when a unit's glyph key is missing).
  flock: {
    1: '/glyph-assets/flock-1.png',
    2: '/glyph-assets/flock-2.png',
    3: '/glyph-assets/flock-3.png',
  },
  // Image-mode units below are registered against PNG paths the asset
  // pipeline will produce when source art lands at assets/units/<unit>/.
  // Until then the paths 404 and the unit renders a broken-image badge
  // — that's the visual cue that art is still pending.
  bear: {
    1: '/glyph-assets/bear-1.png',
    2: '/glyph-assets/bear-2.png',
    3: '/glyph-assets/bear-3.png',
  },
  boar: {
    1: '/glyph-assets/boar-1.png',
    2: '/glyph-assets/boar-2.png',
    3: '/glyph-assets/boar-3.png',
  },
  wyrm: {
    1: '/glyph-assets/wyrm-1.png',
    2: '/glyph-assets/wyrm-2.png',
    3: '/glyph-assets/wyrm-3.png',
  },
  caiman: {
    1: '/glyph-assets/caiman-1.png',
    2: '/glyph-assets/caiman-2.png',
    3: '/glyph-assets/caiman-3.png',
  },
  hawk: {
    1: '/glyph-assets/hawk-1.png',
    2: '/glyph-assets/hawk-2.png',
    3: '/glyph-assets/hawk-3.png',
  },
  whale: {
    1: '/glyph-assets/whale-1.png',
    2: '/glyph-assets/whale-2.png',
    3: '/glyph-assets/whale-3.png',
  },
  serpent: {
    1: '/glyph-assets/serpent-1.png',
    2: '/glyph-assets/serpent-2.png',
    3: '/glyph-assets/serpent-3.png',
  },
  walking_stick: {
    1: '/glyph-assets/walking_stick-1.png',
    2: '/glyph-assets/walking_stick-2.png',
    3: '/glyph-assets/walking_stick-3.png',
  },
  // Beetle (Hive Queen) — all three tiers from
  // assets/hive_queen_3tier.png (already alpha-stripped; flipped to
  // face right via extract-tier-art's flipHorizontal). Only
  // hive_queen uses the `beetle` glyph key, so registering it here
  // takes the bitmap path on shop card / board / encyclopedia
  // instead of the legacy SVG Beetle silhouette.
  beetle: {
    1: '/glyph-assets/beetle-1.png',
    2: '/glyph-assets/beetle-2.png',
    3: '/glyph-assets/beetle-3.png',
  },
  // Frog (Pond Frog) — all three tiers from assets/pond_frog_3tier.jpg.
  // Only pond_frog uses the `frog` glyph key, so this takes the
  // bitmap path on shop card / board / encyclopedia instead of the
  // legacy SVG Frog silhouette.
  frog: {
    1: '/glyph-assets/frog-1.png',
    2: '/glyph-assets/frog-2.png',
    3: '/glyph-assets/frog-3.png',
  },
  // Tiger (Bulwark Tiger) — all three tiers from
  // assets/bulwark_tiger_3tier.jpg. Only bulwark_tiger uses the
  // `tiger` glyph key, so this takes the bitmap path on shop card /
  // board / encyclopedia instead of the legacy SVG Tiger silhouette.
  tiger: {
    1: '/glyph-assets/tiger-1.png',
    2: '/glyph-assets/tiger-2.png',
    3: '/glyph-assets/tiger-3.png',
  },
  // Fish (Brook Carp) — all three tiers from assets/brook_carp_3tier.jpg.
  // Only brook_carp uses the `fish` glyph key (koi has its own
  // separate glyph), so this takes the bitmap path on shop card /
  // board / encyclopedia instead of the legacy SVG Fish silhouette.
  fish: {
    1: '/glyph-assets/fish-1.png',
    2: '/glyph-assets/fish-2.png',
    3: '/glyph-assets/fish-3.png',
  },
  // Snake (Boon Adder) — all three tiers from assets/boon_adder_3tier.jpg.
  // Only boon_adder uses the `snake` glyph now that Rear Sentinel
  // was renamed to Pterodactyl with its own glyph key. Takes the
  // bitmap path on shop card / board / encyclopedia instead of the
  // legacy SVG Snake silhouette.
  snake: {
    1: '/glyph-assets/snake-1.png',
    2: '/glyph-assets/snake-2.png',
    3: '/glyph-assets/snake-3.png',
  },
  // Pterodactyl (formerly Rear Sentinel) — all three tiers from
  // assets/pterodactyl_3tier.jpg. New `pterodactyl` glyph key added
  // to GlyphKeySchema for this unit; the legacy `snake` glyph was
  // dropped because Vine Serpent already owns the snake silhouette.
  pterodactyl: {
    1: '/glyph-assets/pterodactyl-1.png',
    2: '/glyph-assets/pterodactyl-2.png',
    3: '/glyph-assets/pterodactyl-3.png',
  },
  // Tally Hawk (formerly Tally Owl) — all three tiers from
  // assets/tally_hawk_3tier.jpg. New `tally_hawk` glyph key
  // distinguishes this perched profile from Storm Hawk's
  // wings-spread `hawk` art; the old UNIT_TINTS entry that recolored
  // Sage Owl's bitmap for Tally Owl was dropped at the same time.
  tally_hawk: {
    1: '/glyph-assets/tally_hawk-1.png',
    2: '/glyph-assets/tally_hawk-2.png',
    3: '/glyph-assets/tally_hawk-3.png',
  },
  // Pollen Bee — three tiers from assets/pollen_bee_3tier.jpg. Flying
  // bees with no ground contact, so no shadow-merge gotchas; standard
  // white-strip + component pruning with clean ~39px valleys at
  // x=918..956 / x=1775..1815. Right-facing (no flip).
  bee: {
    1: '/glyph-assets/bee-1.png',
    2: '/glyph-assets/bee-2.png',
    3: '/glyph-assets/bee-3.png',
  },
  // Tide Skink — three tiers from assets/skink_3tier.jpg. Clean ~84px
  // valleys between figures at x=64..421 / x=504..861 / x=946..1304;
  // standard white-strip + component pruning. Right-facing (no flip).
  skink: {
    1: '/glyph-assets/skink-1.png',
    2: '/glyph-assets/skink-2.png',
    3: '/glyph-assets/skink-3.png',
  },
  // Reed Swallow — three tiers from assets/swallow_3tier.jpg. Clean
  // ~37px valleys between figures at x=41..449 / x=485..892 /
  // x=929..1336; standard white-strip + component pruning. Figures
  // face right (no flip).
  swallow: {
    1: '/glyph-assets/swallow-1.png',
    2: '/glyph-assets/swallow-2.png',
    3: '/glyph-assets/swallow-3.png',
  },
  // Pebble Quail — three tiers from assets/quail_3tier.jpg. Ground
  // shadows touch in the valleys, so use `alphaCutoff: 180` to thin
  // shadow halos enough that component pruning isolates each figure.
  // Figures face right (no flip).
  quail: {
    1: '/glyph-assets/quail-1.png',
    2: '/glyph-assets/quail-2.png',
    3: '/glyph-assets/quail-3.png',
  },
  // Cinderfly uses the `butterfly` glyph. Three tiers from
  // assets/cinderfly_3tier.jpg — figures at x=46..421, x=459..841,
  // x=881..1288 on a clean white backdrop; default white-strip + the
  // component-pruning pass handles the bleed in the wide crops.
  butterfly: {
    1: '/glyph-assets/butterfly-1.png',
    2: '/glyph-assets/butterfly-2.png',
    3: '/glyph-assets/butterfly-3.png',
  },
  // Banky Pig — three tiers from assets/banky_pig_3tier.jpg
  // (1344x768, neutral-grey backdrop with achromatic-strip + chromatic
  // bodies). Standalone glyph key so no clash with Pebble Pup's `pup`.
  pig: {
    1: '/glyph-assets/pig-1.png',
    2: '/glyph-assets/pig-2.png',
    3: '/glyph-assets/pig-3.png',
  },
  // Gold Fish — three tiers from assets/gold_fish_3tier.jpg
  // (1376x768, pure-white backdrop, deeply saturated bodies). The
  // `goldfish` glyph key was added to keep Brook Carp's `fish`
  // silhouette unaffected.
  goldfish: {
    1: '/glyph-assets/goldfish-1.png',
    2: '/glyph-assets/goldfish-2.png',
    3: '/glyph-assets/goldfish-3.png',
  },
  // Wolf Spider — three tiers from assets/wolf_spider_3tier.jpg
  // (1376x768, warm-cream backdrop, moderate-chroma bodies; same
  // strip recipe as fox / brook carp). The `spider` glyph key was
  // added to keep Mantis Acolyte's `mantis` silhouette unaffected.
  spider: {
    1: '/glyph-assets/spider-1.png',
    2: '/glyph-assets/spider-2.png',
    3: '/glyph-assets/spider-3.png',
  },
}
